import type { HttpService } from '../App/services/http-service';
import type { DataModelService } from '../Shell/PandoraSolutionBuilderAgent/services/data-model-service';
import type { ContentWidgetService } from '../Shell/PandoraSolutionBuilderAgent/services/content-widget-service';
import { ContentWidgetViewType } from '../Shell/PandoraSolutionBuilderAgent/model/enumerations';

export interface AdaptiveCardWidgetTypeDTO {
	WidgetId: string;
	Category: string;
	CategoryName: string;
	Image: string;
	Title: string;
	MinHeight: number;
	MinWidth: number;
	IsAdaptiveCard: boolean;
	Id: string;
	Name: string;
	Template: {
		WidgetType: number;
		Id: string;
	};
	System: boolean;
	Enabled: boolean;
	Tag: string;
	WidgetType: number;
	Applications: string;
	HelpValue: string;
}

export interface AdaptiveCardWidgetInstanceDTO {
	WidgetId: string;
	ObjectId: string;
}

export interface AdaptiveCardInstance {
	trackById: number;
	objectId: string;
	widgetId: string;
	widgetType: AdaptiveCardWidgetTypeDTO;
	internalTemplate: string;
	serviceContextInfo: { widgetId: string; viewName: string };
	dataModel: unknown;
}

interface AdaptiveCardTemplateData {
	template: string;
	viewKey: string;
	dataModelExtensions: Record<string, unknown>;
}

export class AdaptiveCardsService {
	static readonly $inject = ['mx.SolutionBuilderAgent.Http', 'mx.SolutionBuilderAgent.DataModelService', 'mx.SolutionBuilderAgent.ContentWidgetService'];

	private widgetTypes?: AdaptiveCardWidgetTypeDTO[];
	private readonly widgetTemplates = new Map<string, AdaptiveCardTemplateData>();

	constructor(
		private readonly http: HttpService,
		private readonly dataModelService: ReturnType<typeof DataModelService>,
		private readonly contentWidgetService: ContentWidgetService
	) {}

	public async getWidgetTypes(): Promise<AdaptiveCardWidgetTypeDTO[]> {
		return this.widgetTypes ?? (this.widgetTypes = await this.http.get<AdaptiveCardWidgetTypeDTO[]>('api/widgetadaptivecard'));
	}

	public async getWidgetInstances(): Promise<AdaptiveCardWidgetInstanceDTO[]> {
		return await this.http.get<AdaptiveCardWidgetInstanceDTO[]>('api/widgetadaptivecard/records');
	}

	public async hideAdaptiveCards(objectIds: string[]): Promise<void> {
		await this.http.post('api/widgetadaptivecard/hiderecords', objectIds);
	}

	public async getAdaptiveCards(): Promise<AdaptiveCardInstance[]> {
		const [widgetTypes, instances] = await Promise.all([this.getWidgetTypes(), this.getWidgetInstances()]);
		const widgetTypesById = new Map(widgetTypes.map((widgetType) => [widgetType.Id, widgetType]));

		const cardsToPrepare = instances.reduce<Array<{ instance: AdaptiveCardWidgetInstanceDTO; widgetType: AdaptiveCardWidgetTypeDTO }>>((cards, instance) => {
			const widgetType = widgetTypesById.get(instance.WidgetId);
			if (widgetType) {
				cards.push({ instance, widgetType });
			}
			return cards;
		}, []);

		return Promise.all(cardsToPrepare.map(({ instance, widgetType }) => this.prepareCardInstance(instance, widgetType)));
	}

	// Mirrors the WidgetInstance rendering pipeline used by mx-dashboard-widget (dashboard.service.ts getWidgetTemplateData/refreshWidgetInstance),
	// so an adaptive card record renders via mx-bind-html-compile the same way a regular dashboard widget instance does.
	private async prepareCardInstance(instance: AdaptiveCardWidgetInstanceDTO, widgetType: AdaptiveCardWidgetTypeDTO): Promise<AdaptiveCardInstance> {
		const templateData = await this.getWidgetTemplateData(instance.WidgetId);

		const dataModel = await this.dataModelService.loadDataModel(
			{ ...templateData.dataModelExtensions, widgetInstanceTitle: widgetType.Title },
			{
				WidgetConfiguration: {},
				Context: {
					WidgetId: instance.WidgetId,
					ObjectId: instance.ObjectId,
				},
			}
		);

		return {
			trackById: Math.random(),
			objectId: instance.ObjectId,
			widgetId: instance.WidgetId,
			widgetType,
			internalTemplate: templateData.template,
			serviceContextInfo: { widgetId: instance.WidgetId, viewName: templateData.viewKey },
			dataModel,
		};
	}

	private async getWidgetTemplateData(widgetId: string): Promise<AdaptiveCardTemplateData> {
		const cached = this.widgetTemplates.get(widgetId);
		if (cached) {
			return cached;
		}

		const templateData: AdaptiveCardTemplateData = {
			template: '',
			viewKey: 'widget_view_not_found',
			dataModelExtensions: {},
		};

		const widgetData = await this.contentWidgetService.getHtml(widgetId);
		const widgetView = widgetData?.Template.Views?.find((view) => view.Type === ContentWidgetViewType.WidgetRuntime);
		templateData.template = widgetView?.Template ?? '';
		templateData.viewKey = widgetView?.Key || 'widget_view_not_found';

		try {
			templateData.dataModelExtensions = JSON.parse(widgetData.Template.DataModelExtensions);
		} catch {
			// do nothing
		}

		this.widgetTemplates.set(widgetId, templateData);
		return templateData;
	}
}
